import React, { PropsWithChildren, useMemo } from 'react';
import { FunctionComponent, useContext } from 'react';
import { createPortal } from 'react-dom';
import { prepareProps } from '../../utils/propUtils';
import { FixedContentContainerProviderContext } from '../FixedContentContainerProvider/FixedContentContainerProvider';

export type FixedContentPortalProps = PropsWithChildren<{
  /**
   * default FixedContentPortalType.Default
   */
  type?: FixedContentPortalType;
}>;

export enum FixedContentPortalType {
  Default = 'default',
  Notification = 'notification'
}

/**
 * This component portals the content into an element which is placed at the root of the App.
 * This makes it possible that fixed content (e.g. a dialog) is not restricted by parent containers which create a new stacking context (opacity, transform...).
 */
export const FixedContentPortal: FunctionComponent<FixedContentPortalProps> =
  React.memo((props) => {
    const { type = FixedContentPortalType.Default, children } = prepareProps({
      props
    });

    const context = useContext(FixedContentContainerProviderContext);

    const containerElement = useMemo(() => {
      switch (type) {
        case FixedContentPortalType.Default:
          return context.containerElement;

        case FixedContentPortalType.Notification:
          return context.notificationContainerElement;

        default:
          throw new Error(`unhandled type "${type}"`);
      }
    }, [type, context]);

    if (!containerElement) {
      throw new Error(
        'No containerElement in the FixedContentContainerProviderContext found. Did you forget to wrap your components with a FixedContentContainerProvider?'
      );
    }

    return createPortal(children, containerElement);
  });
